import React, { useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@phosphor-icons/react';

export interface BannerConfig {
  id?: string;
  content: string | ReactNode;
  type?: 'info' | 'warning' | 'success' | 'error';
  showClose?: boolean;
  actionButton?: {
    text: string;
    onClick: () => void;
  };
  duration?: number; // 自动关闭时间（毫秒），0 表示不自动关闭
}

export interface BannerState {
  banner: BannerConfig | null;
  showBanner: (config: BannerConfig) => void;
  hideBanner: () => void;
}

export const bannerInitialState: BannerState = {
  banner: null,
  showBanner: () => {},
  hideBanner: () => {},
};

interface GlobalBannerProps extends BannerState {
  containerId?: string; // 可选的容器 ID，用于 Portal 目标
}

const GlobalBanner: React.FC<GlobalBannerProps> = ({
  banner,
  hideBanner,
  containerId = 'admin-banner-container'
}) => {
  useEffect(() => {
    if (banner?.duration && banner.duration > 0) {
      const timer = setTimeout(() => {
        hideBanner();
      }, banner.duration);

      return () => clearTimeout(timer);
    }
  }, [banner, hideBanner]);

  if (!banner) {
    return null;
  }

  const bannerContent = (
    <div className="w-full bg-[#F4F4F5] p-2 flex items-center justify-center relative overflow-hidden">
      <div className="flex items-center gap-2">
        {banner.content}
      </div>

      <div className="absolute right-4 flex items-center gap-3">
        {banner.actionButton && (
          <button
            className="px-3 py-1 bg-[#1A162F] text-white border border-transparent rounded-lg text-xs font-medium hover:bg-[#1A162F]/90 transition-colors shadow-sm"
            onClick={banner.actionButton.onClick}
          >
            {banner.actionButton.text}
          </button>
        )}
        {banner.showClose !== false && (
          <button
            className="text-[#666F8D] hover:text-[#1A162F] p-1 rounded-md hover:bg-gray-200/50 transition-colors"
            onClick={hideBanner}
            aria-label="Close banner"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );

  // 查找目标容器
  const container = document.getElementById(containerId);

  // 如果找到了容器，Portal 到容器中；否则渲染到 body
  return createPortal(bannerContent, container || document.body);
};

export default GlobalBanner;
